import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './routes/ProtectedRoute';
import AdminRoute from './routes/AdminRoute';
import Layout from './components/Layout';

import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { PasswordResetConfirmPage } from './pages/PasswordResetConfirmPage';
import { CreateAccountPage } from './pages/CreateAccountPage';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { ContactUsPage } from './pages/ContactUsPage';
import { SchedulePage } from './pages/SchedulePage';
import { StandingsPage } from './pages/StandingsPage';
import { PointSeriesPage } from './pages/PointSeriesPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { LeagueSignUpPage } from './pages/LeagueSignUpPage';
import { MyTeamPage } from './pages/MyTeamPage';
import { MembersPage } from './pages/MembersPage';
import { TeamSetupPage } from './pages/TeamSetupPage';
import { LeagueTeamsPage } from './pages/LeagueTeamsPage';
import { TeamDetailsPage } from './pages/TeamDetailsPage';
import { ScheduleBuilderPage } from './pages/ScheduleBuilderPage';
import { PlayoffBuilderPage } from './pages/PlayoffBuilderPage';
import { UpdateSchedulePage } from './pages/UpdateSchedulePage';
import { PlayerStatsPage } from './pages/PlayerStatsPage';
import { PlayerStatsCardsPage } from './pages/PlayerStatsCardsPage';
import { UploadMatchResultsPage } from './pages/UploadMatchResultsPage';
import { MatchResultsSummaryPage } from './pages/MatchResultsSummaryPage';
import { CompareStatsPage } from './pages/CompareStatsPage';
import { TeamStatsPage } from './pages/TeamStatsPage';
import { TeamStatsCardsPage } from './pages/TeamStatsCardsPage';
//import { ArchivedPlayersPage } from './pages/ArchivedPlayersPage';
//import { HighlightsPage } from './pages/HighlightsPage';
import { PointSeriesAdminPage } from './pages/PointSeriesAdminPage';
import { MemberProfilePage } from './pages/MemberProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminManageUserPage from './pages/AdminManageUserPage';
import { SettingsPage } from './pages/SettingsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfUsePage } from './pages/TermsOfUsePage';

import { auth, db } from './firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

const AppContent = () => {
  const { loading, currentUser } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Routes with shared layout */}
        <Route path="/" element={<Layout />}>
          {/* Public routes */}
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactUsPage />} />
          <Route path="schedule" element={<ScheduleRoute />} />
          <Route path="reset-password" element={<ResetPasswordRoute />} />
          <Route path="reset-password/confirm" element={<PasswordResetConfirmRoute />} />
          {/* Auth pages inside layout so header/menu show */}
          <Route path="login" element={currentUser ? <Navigate to="/schedule" replace /> : <LoginRoute />} />
          <Route path="create-account" element={<CreateAccountRoute />} />
          <Route path="standings" element={<StandingsPage />} />
          <Route path="point-series" element={<PointSeriesPage />} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          <Route path="profile/:username" element={<PublicProfilePage />} />
          <Route path="stats/teams" element={<TeamStatsPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="terms-of-use" element={<TermsOfUsePage />} />
          <Route path="stats/team-stats-cards" element={<TeamStatsCardsPage />} />
          <Route path="league/sign-up" element={<LeagueSignUpPage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="dashboard" element={<DashboardRoute />} />
            <Route path="profile" element={<ProfileRoute />} />
            <Route path="my-team" element={<MyTeamRoute />} />
            <Route path="members" element={<MembersRoute />} />
            <Route path="members/profile" element={<MemberProfileRoute />} />
            <Route path="teams" element={<LeagueTeamsRoute />} />
            <Route path="teams/details" element={<TeamDetailsRoute />} />
            <Route path="stats/players" element={<PlayerStatsPage />} />
            <Route path="stats/player-stats-cards" element={<PlayerStatsCardsPage />} />
            <Route path="stats/compare" element={<CompareStatsRoute />} />
            <Route path="results/summary" element={<MatchResultsSummaryRoute />} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminRoute />}>
            <Route path="admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="admin/manage/:userId" element={<AdminManageUserPage />} />
            <Route path="admin/team-setup" element={<TeamSetupRoute />} />
            <Route path="admin/schedule-builder" element={<ScheduleBuilderRoute />} />
            <Route path="admin/playoff-builder" element={<PlayoffBuilderRoute />} />
            <Route path="admin/update-schedule" element={<UpdateScheduleRoute />} />
            <Route path="admin/upload-results" element={<UploadResultsRoute />} />
            <Route path="admin/settings" element={<SettingsPage />} />
              <Route path="/admin/point-series-tools" element={<PointSeriesAdminPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<div className="text-center">404 - Page Not Found</div>} />
        </Route>

        
      </Routes>
    </BrowserRouter>
  );
};
  // Legacy navigate mapper to preserve old navigate(page, props) usage
  const useLegacyNavigate = () => {
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    return (page, props = {}) => {
      const map = {
        'Home': '/',
        'About': '/about',
        'Contact Us': '/contact',
        'Schedule': '/schedule',
        'Standings': '/standings',
        'Overview': '/point-series',
        'Leaderboard': '/leaderboard',
        'Login': '/login',
        'Create Account': '/create-account',
        'Profile': userProfile?.username ? `/profile/${userProfile.username}` : '/schedule',
        'Dashboard': '/dashboard',
        'Settings': '/admin/settings',
        'League Sign-Up': '/league/sign-up',
        'My Team': '/my-team',
        'Members': '/members',
        'League Teams': '/teams',
        'Team Details': '/teams/details',
        'Team Setup': '/admin/team-setup',
        'Schedule Builder': '/admin/schedule-builder',
        'Playoff Builder': '/admin/playoff-builder',
        'Update Schedule': '/admin/update-schedule',
        'Upload Results': '/admin/upload-results',
        'Match Results Summary': '/results/summary',
        'Player Stats': '/stats/players',
        'Team Stats': '/stats/teams',
        'Team Stats Cards': '/stats/team-stats-cards',
        'Compare Stats': '/stats/compare',
        'Member Profile': '/members/profile',
        'Privacy Policy': '/privacy-policy',
        'Terms of Use': '/terms-of-use',
      };
      const path = map[page] || '/';
      navigate(path, { state: props });
    };
  };

  const MyTeamRoute = () => {
    const { userProfile } = useAuth();
    const legacyNavigate = useLegacyNavigate();
    return <MyTeamPage userRole={userProfile?.role} memberProfile={userProfile} navigate={legacyNavigate} />;
  };

  const MembersRoute = () => {
    const { userProfile } = useAuth();
    const legacyNavigate = useLegacyNavigate();
    return <MembersPage userRole={userProfile?.role} navigate={legacyNavigate} />;
  };

  const MemberProfileRoute = () => {
    const legacyNavigate = useLegacyNavigate();
    const location = useLocation();
    return <MemberProfilePage pageProps={location.state} navigate={legacyNavigate} />;
  };

  const LeagueTeamsRoute = () => {
    const { userProfile } = useAuth();
    const legacyNavigate = useLegacyNavigate();
    return <LeagueTeamsPage userRole={userProfile?.role} navigate={legacyNavigate} />;
  };

  const TeamDetailsRoute = () => {
    const { userProfile } = useAuth();
    const legacyNavigate = useLegacyNavigate();
    const location = useLocation();
    return <TeamDetailsPage userRole={userProfile?.role} pageProps={location.state} navigate={legacyNavigate} />;
  };

  const TeamSetupRoute = () => {
    const { userProfile } = useAuth();
    return <TeamSetupPage userRole={userProfile?.role} />;
  };

  const ScheduleBuilderRoute = () => {
    const { userProfile } = useAuth();
    return <ScheduleBuilderPage userRole={userProfile?.role} />;
  };

  const UpdateScheduleRoute = () => {
    const { userProfile } = useAuth();
    return <UpdateSchedulePage userRole={userProfile?.role} />;
  };

  const PlayoffBuilderRoute = () => {
    const { userProfile } = useAuth();
    return <PlayoffBuilderPage userRole={userProfile?.role} />;
  };

  const UploadResultsRoute = () => {
    const { userProfile } = useAuth();
    return <UploadMatchResultsPage userRole={userProfile?.role} />;
  };

  const MatchResultsSummaryRoute = () => {
    const legacyNavigate = useLegacyNavigate();
    const location = useLocation();
    return <MatchResultsSummaryPage matchData={location.state} navigate={legacyNavigate} />;
  };

  const CompareStatsRoute = () => {
    const { userProfile } = useAuth();
    return <CompareStatsPage loggedInUser={userProfile} userRole={userProfile?.role} />;
  };

  const ScheduleRoute = () => {
    const { userProfile } = useAuth();
    const legacyNavigate = useLegacyNavigate();
    return <SchedulePage loggedInUser={userProfile} navigate={legacyNavigate} />;
  };

  const DashboardRoute = () => {
    const { userProfile } = useAuth();
    const legacyNavigate = useLegacyNavigate();
    return <DashboardPage userRole={userProfile?.role} loggedInUser={userProfile} navigate={legacyNavigate} />;
  };

  const ProfileRoute = () => {
    const { userProfile } = useAuth();
    return <ProfilePage userRole={userProfile?.role} memberProfile={userProfile} setLoggedInUser={() => {}} />;
  };

  const LoginRoute = () => {
    const navigate = useNavigate();
    const onLogin = async (email, password) => {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (cred.user) navigate('/schedule', { replace: true });
    };
    return <LoginPage onLogin={onLogin} onCreateAccount={() => navigate('/create-account')} onForgotPassword={() => navigate('/reset-password')} />;
  };

  const CreateAccountRoute = () => {
    const navigate = useNavigate();
    const onCreateAccount = async (email, password, firstName, lastName, role = 'member') => {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const user = cred.user;
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        email,
        firstName,
        lastName,
        role,
        nickname: '',
        profileImage: 'default',
        dateJoined: new Date().toISOString(),
        dartInfo: {},
        homeSetup: {},
        matchHistory: [],
        formerTeams: [],
      });
      navigate('/schedule', { replace: true });
    };
    return <CreateAccountPage onCreateAccount={onCreateAccount} onNavigate={(page) => navigate(page)} />;
  };

  const App = () => {
    return (
      <AuthProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    );
  };

  const ResetPasswordRoute = () => {
    return <ResetPasswordPage />;
  };

  const PasswordResetConfirmRoute = () => {
    return <PasswordResetConfirmPage />;
  };

  export default App;